import { useCallback, useMemo, useState } from "react";
import LargePopupModalHeader from "../../../Containers/LargePopupModalHeader";
import RectangleButton from "../../../Buttons/RectangleButton";
import { Colors } from "../../../../utils/colors";
import { MassTextsActionItem } from "../Items/MassTextsActionItem";
import { Icon } from "@iconify/react";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import {
  Event,
  EventType,
  MassTextAttachmentId,
  MembershipState,
} from "@markit/common.types";
import {
  hasSubscription,
  isApprovedPhoneStatus,
  isEventRelatedType,
} from "@markit/common.utils";
import SelectProductItemModal from "../../Products/SelectProductItemModal";
import { useSelector } from "react-redux";
import { getEventState } from "../../../../redux/slices/eventSlice";
import ConfirmDeleteModal from "../../../Containers/ConfirmPopups/ConfirmDeleteModal";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { getProductDetails } from "../../../../utils/productUtils";

type MassTextAttachmentsPanelProps = {
  closeOnPress: () => void;
  defaultListView: number;
  defaultAttachmentId: string | undefined;
  finalizeAttachment: (attachmentItemId: string) => void;
  event?: Event;
};

const MassTextAttachmentsPanel = (props: MassTextAttachmentsPanelProps) => {
  const {
    closeOnPress,
    defaultListView,
    defaultAttachmentId,
    finalizeAttachment,
    event,
  } = props;
  const { accountData } = useSelector(getAccountState).account;
  const { events } = useSelector(getEventState).events;
  const [itemId, setItemId] = useState<string | undefined>(defaultAttachmentId);
  const [selectedEventType, setSelectedEventType] = useState<
    EventType | undefined
  >(undefined);
  const [listView, setListView] = useState(defaultListView || 0);
  const [showConfirmBackModal, setShowConfirmBackModal] = useState(false);

  const userEvents = useMemo(() => {
    return events.filter((event) => !event.isDraft);
  }, [events]);

  const madeChanges = useMemo(() => !!itemId, [itemId]);

  const hasPaidMembership = useMemo(
    () =>
      accountData.membership.state === MembershipState.ACTIVE &&
      accountData.membership.membershipPlans.some((plan) => plan.price > 0),
    [accountData.membership.membershipPlans, accountData.membership.state]
  );

  const hasContactCard = useMemo(
    () => hasSubscription(accountData) && isApprovedPhoneStatus(accountData),
    [accountData]
  );

  const confirmBackOnPress = useCallback(() => {
    if (selectedEventType) {
      setSelectedEventType(undefined);
    } else {
      closeOnPress();
    }
  }, [selectedEventType, setSelectedEventType, closeOnPress]);

  const backOnPress = useCallback(() => {
    if (madeChanges && !selectedEventType) {
      setShowConfirmBackModal(true);
    } else {
      confirmBackOnPress();
    }
  }, [madeChanges, selectedEventType, confirmBackOnPress]);

  const attachOnPress = useCallback(() => {
    if (itemId) {
      finalizeAttachment(itemId);
    }
  }, [finalizeAttachment, itemId]);

  const ActionItemDivider = useMemo(
    () => (
      <div style={{ paddingLeft: 62 }}>
        <HorizontalDivider altMargin={7} />
      </div>
    ),
    []
  );

  const renderActionItem = useCallback(
    (
      title: string,
      subtext: string,
      iconName: string,
      attachmentId: MassTextAttachmentId,
      disabled: boolean
    ) => (
      <MassTextsActionItem
        title={title}
        subtext={subtext}
        icon={
          <Icon icon={iconName} height={28} style={{ color: Colors.BLACK }} />
        }
        onPress={() => setItemId(itemId === attachmentId ? "" : attachmentId)}
        isCheckSelected={itemId === attachmentId}
        checkColor={Colors.BLACK}
        disabled={disabled}
      />
    ),
    [itemId]
  );

  return (
    <>
      <SelectProductItemModal
        headerComp={
          <LargePopupModalHeader
            title={
              selectedEventType
                ? getProductDetails(selectedEventType).title
                : "Attach"
            }
            altHeaderLeft={selectedEventType ? "ion:chevron-back" : undefined}
            backOnPress={backOnPress}
            headerRight={
              <RectangleButton
                buttonLabel={"Add"}
                onPress={attachOnPress}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                disabled={!madeChanges}
                containerStyles={{
                  borderRadius: 100,
                  padding: "10px 14px",
                  height: 36,
                }}
              />
            }
          />
        }
        generalTabComp={
          <div className="ColumnNormal">
            {renderActionItem(
              "Contact Card",
              hasContactCard
                ? "Savable contact card with name, picture, and number"
                : "Available with Markit+ Pro Plan",
              "ion:id-card",
              MassTextAttachmentId.CONTACT_CARD,
              !hasContactCard
            )}
            {ActionItemDivider}
            {renderActionItem(
              "Profile Link",
              "A link to your profile page",
              "ion:person-circle",
              MassTextAttachmentId.PROFILE_LINK,
              false
            )}
            {ActionItemDivider}
            {renderActionItem(
              "Membership Link",
              hasPaidMembership
                ? "A link to your membership page"
                : "Enable paid memberships",
              "ion:ribbon",
              MassTextAttachmentId.MEMBERSHIP_LINK,
              !hasPaidMembership
            )}
            {event ? (
              <>
                {ActionItemDivider}
                {renderActionItem(
                  "Order Link",
                  "Unique link for each recipient's order confirmation",
                  "ion:ticket",
                  MassTextAttachmentId.TICKET_LINK,
                  false
                )}
                {isEventRelatedType(event.eventType) ? (
                  <>
                    {ActionItemDivider}
                    {renderActionItem(
                      "Calendar Invite",
                      "Unique link for each recipient to save their ticket to calendar",
                      "ion:calendar",
                      MassTextAttachmentId.CALENDAR_INVITE,
                      false
                    )}
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        }
        items={itemId ? [itemId] : []}
        setItems={(newItem: string) => {
          if (itemId === newItem) {
            setItemId(undefined);
          } else {
            setItemId(newItem);
          }
        }}
        selectedEventType={selectedEventType}
        setSelectedEventType={setSelectedEventType}
        userEvents={userEvents}
        listView={listView}
        setListView={setListView}
        showHiddenLabel={false}
      />
      <ConfirmDeleteModal
        heading="If you go back, your changes will be discarded."
        subtext="Continue?"
        deleteButtonText="Discard Changes"
        hideModal={!showConfirmBackModal}
        setIsVisible={setShowConfirmBackModal}
        deleteOnPress={confirmBackOnPress}
      />
    </>
  );
};

export default MassTextAttachmentsPanel;
